
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { AppDispatch, RootState } from '../../../../app/store';
import { Group, MemberGroup, getUserGroups } from '../../../../app/features/sessions/sessionGroups/sessionGroups';
import CircularProgress from '@mui/material/CircularProgress';
import { CreateGroupForm } from '../../../../app/components/forms/groups/CreateGroupForm';
import { UserAvatar } from '../../../../app/components/userAvatar/UserAvatar';
import { GroupDetailsModal } from '../../../../app/components/modal/groupModal/GroupDetailsModal';
import LoadingModalScreenContainer from '../../../../app/components/modal/loadingModalScreenContainer/LoadingModalScreenContainer';
import * as REQUEST_REQUIREMENTS from '../../../../app/api/requestRequirements';
import { User } from '../../../../app/#interfaces/interfaces';
import {
    Box,
    Chip,
    Container,
    IconButton,
    Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import GroupIcon from '@mui/icons-material/Group';

const BG_MAIN        = '#15151a';
const BG_SECTION     = '#1c1c24';
const BG_CARD        = '#24242e';
const BRAND_RED      = '#c0392b';
const TEXT_PRIMARY   = '#f4f4f6';
const TEXT_SECONDARY = 'rgba(244,244,246,0.65)';
const BORDER_SUBTLE  = '1px solid rgba(255,255,255,0.08)';

function MemberGroups() {
    const { userNicknameRoute } = useParams();
    const navigate = useNavigate();
    const authHeaders = useSelector((state: RootState) => state.session.authHeaders);
    const currentUser = useSelector((state: RootState) => state.session.currentUser);
    const userGroups = useSelector((state: RootState) => state.sessionGroups.userGroups);
    const newUserGroups = useSelector((state: RootState) => state.sessionGroups.newUserGroups);
    const dispatch = useDispatch<AppDispatch>();
    const userGroupsCreated = [1];
    const noGroupsCondition = (userGroups && userGroups.length === 0) && (userGroupsCreated && userGroupsCreated.length === 0);
    const [page, setPage] = useState(1);
    const [loadingMoreGroups, setLoadingMoreGroups] = useState(false);
    const visibleItems = 10;
    const [showGroupModal, setShowGroupModal] = useState(false);
    const [groupSelectedToShowDetails, setGroupSelectedToShowDetails] = useState<Group>();
    const [memberGroupSelected, setMemberGroupSelectedToShowDetails] = useState<MemberGroup>();
    const loadingScreen = useSelector((state: RootState) => state.sessionGroups.loadingScreen);

    useEffect(() => {
        async function fetchData() {
            if (authHeaders && userNicknameRoute && !loadingMoreGroups && !noGroupsCondition) {
                setLoadingMoreGroups(true);
                try {
                    await dispatch(getUserGroups({ authHeaders: authHeaders, userNickname: userNicknameRoute, page: page }));
                } catch (error) {
                    console.error('Error fetching data:', error);
                } finally {
                    setLoadingMoreGroups(false);
                }
            }
        }

        fetchData();
    }, [authHeaders, userNicknameRoute, page, noGroupsCondition]);

    useEffect(() => {
        const handleScroll = () => {
            const container = document.getElementById('container-ul-member-groups');
            if (!container) return;

            const { scrollTop, clientHeight, scrollHeight } = container;
            if (scrollTop + clientHeight >= scrollHeight && !loadingMoreGroups) {
                setPage(page + 1);
            }
        };

        setTimeout(() => {
            const container = document.getElementById('container-ul-member-groups');
            if (container) {
                container.addEventListener('scroll', handleScroll);
                return () => container.removeEventListener('scroll', handleScroll);
            }
        }, 100);
    }, [loadingMoreGroups]);

    async function handleNavigateEnterGroup() {
        if (groupSelectedToShowDetails) {
            const PRIVATE_ROUTES = REQUEST_REQUIREMENTS.handlePrivateRoutes({
                ROUTE_PARAMS: {
                    userNickname: memberGroupSelected?.user.nickname,
                    groupId: groupSelectedToShowDetails.id,
                },
            });
            navigate(PRIVATE_ROUTES.GROUP_UPDATE_SHOW);
        }
    }

    async function handleNavigationUserProfile(userTarget: User) {
        if (userTarget) {
            const routeParams = { userNickname: userTarget.userMoreInfo?.nickname };
            const PRIVATE_ROUTES = await REQUEST_REQUIREMENTS.handlePrivateRoutes({ ROUTE_PARAMS: routeParams });
            return navigate(PRIVATE_ROUTES.USER);
        }
    }

    function renderGroupCard(item: MemberGroup, index: number) {
        return (
            <Box
                key={index}
                sx={{
                    bgcolor: BG_SECTION,
                    border: BORDER_SUBTLE,
                    borderRadius: '10px',
                    mb: 1.5,
                    overflow: 'hidden',
                }}
            >
                {/* Cabeçalho do card */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: { xs: 1.5, sm: 2 },
                        pt: 1.5,
                        pb: 1,
                        flexWrap: 'wrap',
                        gap: 1,
                    }}
                >
                    {/* Esquerda: nome + membros */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            variant='subtitle1'
                            fontWeight={700}
                            color={TEXT_PRIMARY}
                            noWrap
                            sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
                        >
                            {item.group.name}
                        </Typography>
                        <Typography variant='caption' color={TEXT_SECONDARY}>
                            {item.group.nMembers} {item.group.nMembers === 1 ? 'member' : 'members'}
                        </Typography>
                    </Box>

                    {/* Centro: dono */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                            cursor: 'pointer',
                            '&:hover span': { textDecoration: 'underline' },
                        }}
                        onClick={() => handleNavigationUserProfile(item.user)}
                    >
                        <UserAvatar src={item.user.userMoreInfo?.avatarUrl} size={28} />
                        <Typography
                            component='span'
                            variant='caption'
                            fontWeight={600}
                            color={TEXT_SECONDARY}
                            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                        >
                            {item.user.nickname}
                        </Typography>
                    </Box>

                    {/* Direita: botão detalhes */}
                    <IconButton
                        size='small'
                        onClick={() => {
                            setShowGroupModal(true);
                            setGroupSelectedToShowDetails(item.group);
                            setMemberGroupSelectedToShowDetails(item);
                        }}
                        sx={{
                            color: TEXT_SECONDARY,
                            bgcolor: BG_CARD,
                            border: BORDER_SUBTLE,
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)', color: TEXT_PRIMARY },
                        }}
                    >
                        <InfoOutlinedIcon fontSize='small' />
                    </IconButton>
                </Box>

                {/* Descrição */}
                {item.group.description && (
                    <Box sx={{ px: { xs: 1.5, sm: 2 }, pb: 1 }}>
                        <Typography
                            variant='body2'
                            color={TEXT_SECONDARY}
                            sx={{
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                maxHeight: '4.5em',
                                overflow: 'hidden',
                                fontSize: { xs: '0.75rem', sm: '0.8rem' },
                            }}
                        >
                            {item.group.description}
                        </Typography>
                    </Box>
                )}

                {/* Géneros musicais */}
                {item.group.groupMusicalGenres && item.group.groupMusicalGenres.length > 0 && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 0.5,
                            px: { xs: 1.5, sm: 2 },
                            pb: 1.5,
                        }}
                    >
                        {item.group.groupMusicalGenres.slice(0, visibleItems).map((gm, i) => (
                            <Chip
                                key={i}
                                label={gm.musicGenre.description}
                                size='small'
                                sx={{
                                    bgcolor: 'rgba(255,255,255,0.07)',
                                    color: TEXT_PRIMARY,
                                    fontSize: '0.65rem',
                                }}
                            />
                        ))}
                        {item.group.groupMusicalGenres.length > visibleItems && (
                            <Chip
                                label={`+${item.group.groupMusicalGenres.length - visibleItems}`}
                                size='small'
                                sx={{
                                    bgcolor: 'rgba(255,255,255,0.05)',
                                    color: TEXT_SECONDARY,
                                    fontSize: '0.65rem',
                                }}
                            />
                        )}
                    </Box>
                )}
            </Box>
        );
    }

    const allGroups = [...(newUserGroups ?? []), ...(userGroups ?? [])];

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: BG_MAIN, py: { xs: 2, sm: 3 } }}>
            {showGroupModal && groupSelectedToShowDetails && (
                <GroupDetailsModal
                    group={groupSelectedToShowDetails}
                    open={setShowGroupModal}
                    enterGroup={handleNavigateEnterGroup}
                />
            )}

            <Container maxWidth='lg'>
                {/* Título */}
                <Box sx={{ mb: 3 }}>
                    <Typography
                        variant='h5'
                        fontWeight={700}
                        color={TEXT_PRIMARY}
                        sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }}
                    >
                        My Groups
                        <Box
                            component='span'
                            sx={{
                                display: 'inline-block',
                                width: 36,
                                height: 3,
                                bgcolor: BRAND_RED,
                                borderRadius: 2,
                                ml: 1.5,
                                verticalAlign: 'middle',
                            }}
                        />
                    </Typography>
                </Box>

                {/* Botão criar grupo */}
                {currentUser && currentUser.nickname === userNicknameRoute && (
                    <Box sx={{ mb: 2 }}>
                        <CreateGroupForm />
                    </Box>
                )}

                {/* Lista de grupos */}
                {loadingScreen ? (
                    <LoadingModalScreenContainer />
                ) : noGroupsCondition ? (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            py: 8,
                            gap: 2,
                        }}
                    >
                        <GroupIcon sx={{ fontSize: 56, color: TEXT_SECONDARY, opacity: 0.4 }} />
                        <Typography variant='body1' color={TEXT_SECONDARY}>
                            No groups yet
                        </Typography>
                    </Box>
                ) : (
                    <Box
                        id='container-ul-member-groups'
                        sx={{
                            maxHeight: '70vh',
                            overflowY: 'auto',
                            pr: 0.5,
                            scrollbarWidth: 'thin',
                            scrollbarColor: 'rgba(255,255,255,0.15) transparent',
                        }}
                    >
                        {allGroups.map((item, index) => renderGroupCard(item, index))}

                        {loadingMoreGroups && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                <CircularProgress size={24} sx={{ color: BRAND_RED }} />
                            </Box>
                        )}
                    </Box>
                )}
            </Container>
        </Box>
    );
}

export default MemberGroups;
